import { escapeHtml, exact, fileSize, fromNow, secondsAsYears, stamp, unix } from './format'
import { assignCellHtml, unassignedRowClass } from './UnassignedFirst'
import { autoBadgeHtml, suggestionHtml } from './ownerMarks'

/**
 * Coloanele listelor Inventoree - InventoreeDevicesModel din public/js/inventoree.js,
 * plus cele doua adaugate de od_ux.js (OdLastActivity, OdHostType) - cu
 * ACELASI HTML pe celula. `device_icon` si `actions` vin gata facute de la
 * server (aceleasi clase PHP ca in portal); restul se scrie aici, ca in
 * model, dar cu valorile scapate (in portal nu erau).
 */

export interface DeviceRow {
  deviceId: number
  DT_RowId: string
  Tip: number
  device_type: string
  device_icon: string
  actions: string
  Denumire: string
  ownerId: number
  Prenume: string | null
  Nume: string | null
  UserLogat: string | null
  office: string | null
  DeviceGroup: string | null
  brand_name: string | null
  bios_date: string | null
  cpu_model: string | null
  cpu_frequency: string | null
  cpu_cores: string | number | null
  storage_size: number | null
  memory_size: number | null
  upgrades: number
  operating_system: string | null
  Mac: string | null
  performance_score: number | string | null
  performance_band: number
  warranty_expiry_date: string | null
  extended_warranty_expiry_date: string | null
  service_tag: string | null
  Model: string | null
  Serial: string | null
  previous_inventory_number: string | null
  DataAccesareDM: string | null
  provider?: string | null
  imei?: string | null
  screen_size?: string | number | null
  battery_capacity?: string | number | null
  department?: string | null
  host_type_label?: string
  host_device_name?: string | null
  /** Bifa de selectie a randului (pusa de pagina, nu de server). */
  __checked?: boolean
  [key: string]: unknown
}

/**
 * Coloana unei liste. `Row` e randul pe care il trimite CHIAR capatul acelei
 * liste: Inventoree da `DeviceRow`, Overview `ManagedRow`, Discovered randul
 * lui. Fara parametru, coloanele care nu si-l spun raman pe `DeviceRow`, ca
 * pana acum.
 */
export interface Column<Row = DeviceRow> {
  /** Indicele din model (targets) - si cheia starii de vizibilitate. */
  index: number
  /** Titlul din cap (HTML in model; aici text sau HTML). */
  title: string
  titleHtml?: string
  /** `columns[i][name]`, coloana de ordonare pe server. */
  name?: string
  orderable: boolean
  visible: boolean
  width?: number
  /** HTML-ul celulei. */
  html(row: Row): string
  /**
   * Clasa pusa pe RAND din aceasta coloana (`TableColumn.rowClass` din
   * DataTable): se aduna de la toate coloanele VIZIBILE ale randului.
   */
  rowClass?(row: Row): string | undefined
}

const DASH = '<i class="fas fa-minus font-10"></i>'

function textOrDash(value: unknown): string {
  return value ? escapeHtml(value) : DASH
}

/*
 * =============================================================================
 * APARATUL FARA STAPAN: BUTON, NU TEXT
 * =============================================================================
 *
 * Cerut de Roxana, 24 septembrie 2026. Pana acum celula de proprietar a unui
 * aparat nealocat scria textul rosu de mai jos:
 *
 *     '<span class="color-red"><i class="fa fa-user"></i> Unassigned</span>'
 *
 * De azi, in locul lui sta butonul Assign, care deschide ACEEASI fereastra ca
 * butonul din randul desfacut (`UnassignedFirst`) - nu o a doua cale.
 *
 * Textul NU s-a sters, si nu e numai un comentariu: el e si ce se deseneaza
 * cand omul N-ARE dreptul de alocare
 * (`overview.unassigned_details_assign.read`, adica `canAssign` din
 * `/inventoree/unassigned/tally`). Un buton care ar raspunde 403 e mai rau
 * decat textul. Ca sa se puna inapoi cum era, `ownerCellHtml` intoarce numai
 * `UNASSIGNED_TEXT`.
 *
 * Adevarul e cel pe care il folosea deja coloana - `ownerId > 0` - nu steagul
 * `is_unassigned` al fixarii: celula raspunde la aceeasi intrebare ca inainte,
 * doar altfel.
 */

/** Textul rosu care statea in celula de proprietar pana pe 24 septembrie 2026. */
export const UNASSIGNED_TEXT = '<span class="color-red"><i class="fa fa-user"></i> Unassigned</span>'

/**
 * Celula de proprietar: numele cand exista un stapan, altfel butonul Assign
 * (sau textul de pana acum, cand omul n-are dreptul de alocare).
 */
function ownerCellHtml(
  deviceId: number,
  owner: { ownerId?: unknown; Prenume?: unknown; Nume?: unknown; owner_auto?: unknown },
  canAssign: boolean,
): string {
  if (Number(owner.ownerId) > 0) {
    /* Insigna "Auto" cand stapanul a fost pus din AD (cerut de Claudiu, 6 octombrie 2026). */
    return (
      `<span class="color-light-grey"><i class="fa fa-user"></i> ${escapeHtml(owner.Prenume)} ${escapeHtml(owner.Nume)}</span>` +
      autoBadgeHtml(owner.owner_auto)
    )
  }
  return canAssign ? assignCellHtml(deviceId) : UNASSIGNED_TEXT
}

const BANDS: Record<number, { color: string; label: string }> = {
  1: { color: '#F00', label: 'Very Slow' },
  2: { color: '#F0AD4E', label: 'Slow' },
  3: { color: '#337AB7', label: 'Fair' },
  4: { color: '#359C45', label: 'Fast' },
}

/** OdLastActivity.render */
export function lastActivityHtml(value: unknown, now: Date = new Date()): string {
  const when = stamp(value)
  if (!when) {
    return '<span class="od-la od-la-never" title="This machine has never reported in.">&mdash;</span>'
  }
  const elapsed = now.getTime() - when.getTime()
  const title = ` title="${exact(when)}"`
  if (elapsed >= 0 && elapsed < 5 * 1000) {
    return `<span class="od-la od-la-live"${title}><span class="od-la-dot" aria-hidden="true"></span>Live</span>`
  }
  if (elapsed > 8 * 60 * 60 * 1000) {
    return `<span class="od-la od-la-stale"${title}>${fromNow(when, now)}</span>`
  }
  return `<span class="od-la"${title}>${fromNow(when, now)}</span>`
}

/** Randul, cat il vede OdHostType - si Inventoree, si Overview. */
export interface HostTypeCells {
  host_type_label?: string
  host_device_name?: string | null
}

/** OdHostType.render */
export function hostTypeHtml(row: HostTypeCells): string {
  const label = row.host_type_label
  const host = row.host_device_name
  if (!label) return DASH
  if (host) {
    return `<span data-toggle="tooltip" data-placement="top" title="Host: ${escapeHtml(host)}">${escapeHtml(label)}</span>`
  }
  return escapeHtml(label)
}

/** easyPieChart(size 20, lineWidth 10, trackColor #DDD): un disc plin, ca in portal, dar in SVG. */
export function performanceHtml(row: DeviceRow): string {
  const raw = row.performance_score
  if (raw === null || raw === undefined || raw === '') {
    return (
      '<i class="fas fa-minus font-10" data-toggle="tooltip" data-placement="top"' +
      ' title="Not enough hardware reported to score this device"></i>'
    )
  }
  const score = parseInt(String(raw), 10)
  const shown = BANDS[Number(row.performance_band)] || { color: '#9AA4B1', label: '' }
  const r = 5
  const c = 2 * Math.PI * r
  const dash = (Math.max(0, Math.min(100, score)) / 100) * c
  return (
    `<span class="easychart mini" data-bar-color="${shown.color}" data-percent="${score}"` +
    ' data-toggle="tooltip" data-placement="top"' +
    ` title="${score}/100${shown.label ? ' - ' + shown.label : ''}" style="display:inline-block;position:relative;width:20px;height:20px;vertical-align:middle">` +
    `<svg width="20" height="20" viewBox="0 0 20 20" aria-hidden="true" style="transform:rotate(-90deg)">` +
    `<circle cx="10" cy="10" r="${r}" fill="none" stroke="#DDD" stroke-width="10"></circle>` +
    `<circle cx="10" cy="10" r="${r}" fill="none" stroke="${shown.color}" stroke-width="10" stroke-dasharray="${dash.toFixed(3)} ${c.toFixed(3)}"></circle>` +
    '</svg>' +
    `<span class="percent_partitions" style="position:absolute;left:0;top:0;width:20px;line-height:20px;text-align:center;font-size:7px;color:#fff">${score}%</span>` +
    '</span>'
  )
}

function ageHtml(row: DeviceRow, now: Date): string {
  const when = stamp(row.bios_date)
  return when ? secondsAsYears(unix(now) - unix(when)) : DASH
}

function warrantyHtml(row: DeviceRow, now: Date): string {
  const ext = stamp(row.extended_warranty_expiry_date)
  const std = stamp(row.warranty_expiry_date)
  if (ext && unix(ext) > unix(now)) return secondsAsYears(unix(ext) - unix(now))
  if (std && unix(std) > unix(now)) return secondsAsYears(unix(std) - unix(now))
  return DASH
}

export function deviceColumns(now: () => Date = () => new Date(), canAssign = false): Column[] {
  return [
    {
      index: 0,
      title: '',
      orderable: false,
      visible: true,
      width: 50,
      html: (r) =>
        `<center class="show_details color-dark-grey pointer font-16" data-id="${r.deviceId}"><i class="fas fa-plus-circle"></i></center>`,
      /* Semnul de pe randul unui aparat nealocat. Sta pe coloana asta fiindca
         ea nu se poate ascunde din meniul "Columns". */
      rowClass: unassignedRowClass,
    },
    {
      index: 1,
      title: 'Type',
      name: 'Tip',
      orderable: true,
      visible: true,
      html: (r) =>
        `<center style="cursor:default;padding-top: 12px" data-toggle="tooltip" data-placement="top" title="${escapeHtml(r.device_type)}">${r.device_icon}</center>` +
        `<center style="visibility: hidden" data-toggle="tooltip" data-placement="top">${escapeHtml(r.device_type)}</center>`,
    },
    { index: 2, title: 'Device name', name: 'Denumire', orderable: true, visible: true, html: (r) => `<p>${escapeHtml(r.Denumire)}</p>` },
    {
      index: 3,
      title: 'Device owner',
      name: 'Prenume',
      orderable: true,
      visible: true,
      /* Aici scria `UNASSIGNED_TEXT` - vezi comentariul de sus (Roxana, 24
         septembrie 2026). Acum e butonul Assign, si textul ramane numai cand
         omul n-are dreptul de alocare. */
      html: (r) => ownerCellHtml(r.deviceId, r, canAssign),
    },
    { index: 4, title: 'Last user logged on', name: 'UserLogat', orderable: true, visible: true, html: (r) => textOrDash(r.UserLogat) },
    { index: 5, title: 'Office', name: 'office', orderable: true, visible: true, html: (r) => textOrDash(r.office) },
    { index: 6, title: 'Group', name: 'DeviceGroup', orderable: true, visible: true, html: (r) => textOrDash(r.DeviceGroup) },
    { index: 7, title: 'Brand', name: 'brand_name', orderable: true, visible: true, html: (r) => textOrDash(r.brand_name) },
    { index: 8, title: 'Device Age', name: 'bios_date', orderable: true, visible: true, html: (r) => ageHtml(r, now()) },
    { index: 9, title: 'CPU model', name: 'cpu_model', orderable: true, visible: true, html: (r) => textOrDash(r.cpu_model) },
    {
      index: 10,
      title: 'CPU frequency',
      name: 'cpu_frequency',
      orderable: true,
      visible: true,
      html: (r) => (r.cpu_frequency ? escapeHtml(r.cpu_frequency) + ' Mhz' : DASH),
    },
    {
      index: 11,
      title: 'CPU cores',
      name: 'cpu_cores_reported',
      orderable: true,
      visible: true,
      html: (r) => (r.cpu_cores ? `<span class="badge">${escapeHtml(r.cpu_cores)}</span>` : DASH),
    },
    { index: 12, title: 'Storage size', name: 'storage_size', orderable: true, visible: true, html: (r) => (r.storage_size ? fileSize(Number(r.storage_size)) : DASH) },
    { index: 13, title: 'Memory size', name: 'memory_size', orderable: true, visible: true, html: (r) => (r.memory_size ? fileSize(Number(r.memory_size)) : DASH) },
    {
      index: 14,
      title: 'Upgrades',
      name: 'upgrades',
      orderable: true,
      visible: true,
      html: (r) => (r.upgrades > 0 ? `<span class="badge badge-info">${r.upgrades}</span>` : '<span class="badge">0</span>'),
    },
    { index: 15, title: 'Operating system', name: 'operating_system', orderable: true, visible: true, html: (r) => textOrDash(r.operating_system) },
    { index: 16, title: 'Mac', name: 'Mac', orderable: true, visible: true, html: (r) => textOrDash(r.Mac) },
    { index: 17, title: 'Performance score', name: 'performance_score', orderable: true, visible: true, html: performanceHtml },
    { index: 18, title: 'Warranty', name: 'warranty_expiry_date', orderable: true, visible: true, html: (r) => warrantyHtml(r, now()) },
    { index: 19, title: 'Service Tag', name: 'service_tag', orderable: true, visible: false, html: (r) => textOrDash(r.service_tag) },
    { index: 20, title: 'Model', name: 'Model', orderable: true, visible: false, html: (r) => textOrDash(r.Model) },
    { index: 21, title: 'Serial', name: 'Serial', orderable: true, visible: false, html: (r) => textOrDash(r.Serial) },
    {
      index: 22,
      title: 'Accounting Inventoree Number',
      name: 'previous_inventory_number',
      orderable: true,
      visible: false,
      html: (r) => textOrDash(r.previous_inventory_number),
    },
    { index: 23, title: 'Actions', orderable: false, visible: true, width: 115, html: (r) => r.actions || DASH },
    {
      index: 24,
      title: '',
      titleHtml: '<center><input type="checkbox" class="allDevices"/></center>',
      orderable: false,
      visible: true,
      width: 50,
      html: (r) => `<center><input value="${r.deviceId}" type="checkbox" class="oneDevice"${r.__checked ? ' checked' : ''}/></center>`,
    },
    { index: 25, title: 'Last Activity', name: 'DataAccesareDM', orderable: true, visible: true, html: (r) => lastActivityHtml(r.DataAccesareDM, now()) },
    { index: 26, title: 'Host Type', orderable: false, visible: false, html: hostTypeHtml },
  ]
}

/** InventoreeMobilesModel + OdLastActivity.appendTo. */
export function mobileColumns(now: () => Date = () => new Date(), canAssign = false): Column[] {
  return [
    {
      index: 0,
      title: '',
      orderable: false,
      visible: true,
      width: 50,
      html: (r) =>
        `<center class="show_details color-dark-grey pointer font-16" data-id="${r.deviceId}"><i class="fas fa-plus-circle"></i></center>`,
      /* Semnul de pe randul unui aparat nealocat. Sta pe coloana asta fiindca
         ea nu se poate ascunde din meniul "Columns". */
      rowClass: unassignedRowClass,
    },
    {
      index: 1,
      title: 'Type',
      name: 'Tip',
      orderable: true,
      visible: true,
      html: (r) =>
        `<center style="cursor:default" data-toggle="tooltip" data-placement="top" title="${escapeHtml(r.device_type)}">${r.device_icon}</center>`,
    },
    { index: 2, title: 'Device name', name: 'Denumire', orderable: true, visible: true, html: (r) => `<p>${escapeHtml(r.Denumire)}</p>` },
    {
      index: 3,
      title: 'Device owner',
      name: 'Prenume',
      orderable: true,
      visible: true,
      /* Aici scria `UNASSIGNED_TEXT` - vezi comentariul de sus (Roxana, 24
         septembrie 2026). Acum e butonul Assign, si textul ramane numai cand
         omul n-are dreptul de alocare. */
      html: (r) => ownerCellHtml(r.deviceId, r, canAssign),
    },
    { index: 4, title: 'Last user logged on', name: 'UserLogat', orderable: true, visible: true, html: (r) => textOrDash(r.UserLogat) },
    { index: 5, title: 'Office', name: 'office', orderable: true, visible: true, html: (r) => textOrDash(r.office) },
    { index: 6, title: 'Group', name: 'DeviceGroup', orderable: true, visible: true, html: (r) => textOrDash(r.DeviceGroup) },
    { index: 7, title: 'Brand', name: 'brand_name', orderable: true, visible: true, html: (r) => textOrDash(r.brand_name) },
    { index: 8, title: 'Provider', name: 'provider', orderable: true, visible: true, html: (r) => textOrDash(r.provider) },
    { index: 9, title: 'CPU model', name: 'cpu_model', orderable: true, visible: true, html: (r) => textOrDash(r.cpu_model) },
    {
      index: 10,
      title: 'CPU frequency',
      name: 'cpu_frequency',
      orderable: true,
      visible: true,
      html: (r) => (r.cpu_frequency ? escapeHtml(r.cpu_frequency) + ' Mhz' : DASH),
    },
    {
      index: 11,
      title: 'CPU cores',
      name: 'cpu_cores_reported',
      orderable: true,
      visible: true,
      html: (r) => (r.cpu_cores ? `<span class="badge">${escapeHtml(r.cpu_cores)}</span>` : DASH),
    },
    { index: 12, title: 'Imei', name: 'imei', orderable: true, visible: true, html: (r) => textOrDash(r.imei) },
    {
      index: 13,
      title: 'Screen size',
      name: 'screen_size',
      orderable: true,
      visible: true,
      html: (r) => (r.screen_size ? escapeHtml(r.screen_size) + 'in' : DASH),
    },
    {
      index: 14,
      title: 'Battery capacity',
      name: 'battery_capacity',
      orderable: true,
      visible: true,
      html: (r) => (r.battery_capacity ? escapeHtml(r.battery_capacity) + 'mA' : DASH),
    },
    { index: 15, title: 'Storage size', name: 'storage_size', orderable: true, visible: true, html: (r) => (r.storage_size ? fileSize(Number(r.storage_size)) : DASH) },
    { index: 16, title: 'Memory size', name: 'memory_size', orderable: true, visible: true, html: (r) => (r.memory_size ? fileSize(Number(r.memory_size)) : DASH) },
    {
      index: 17,
      title: 'Upgrades',
      name: 'upgrades',
      orderable: true,
      visible: true,
      html: (r) => (r.upgrades > 0 ? `<span class="badge badge-info">${r.upgrades}</span>` : '<span class="badge">0</span>'),
    },
    { index: 18, title: 'Warranty', name: 'warranty_expiry_date', orderable: true, visible: true, html: (r) => warrantyHtml(r, now()) },
    { index: 19, title: 'Service Tag', name: 'service_tag', orderable: true, visible: false, html: (r) => textOrDash(r.service_tag) },
    { index: 20, title: 'Model', name: 'Model', orderable: true, visible: false, html: (r) => textOrDash(r.Model) },
    { index: 21, title: 'Serial', name: 'Serial', orderable: true, visible: false, html: (r) => textOrDash(r.Serial) },
    {
      index: 22,
      title: 'Accounting Inventoree Number',
      name: 'previous_inventory_number',
      orderable: true,
      visible: false,
      html: (r) => textOrDash(r.previous_inventory_number),
    },
    { index: 23, title: 'Actions', orderable: false, visible: true, width: 115, html: (r) => r.actions || DASH },
    {
      index: 24,
      title: '',
      titleHtml: '<center><input type="checkbox" class="allDevices"/></center>',
      orderable: false,
      visible: true,
      width: 50,
      html: (r) => `<center><input value="${r.deviceId}" type="checkbox" class="oneDevice"${r.__checked ? ' checked' : ''}/></center>`,
    },
    { index: 25, title: 'Last Activity', name: 'DataAccesareDM', orderable: true, visible: true, html: (r) => lastActivityHtml(r.DataAccesareDM, now()) },
  ]
}

/** InventoreeAgentlessModel. */
export function agentlessColumns(canAssign = false): Column[] {
  return [
    {
      index: 0,
      title: '',
      orderable: false,
      visible: true,
      width: 50,
      html: (r) =>
        `<center class="show_details color-dark-grey pointer font-16" data-id="${r.deviceId}"><i class="fas fa-plus-circle"></i></center>`,
    },
    {
      index: 1,
      title: 'Type',
      name: 'Tip',
      orderable: true,
      visible: true,
      html: (r) =>
        `<p style="cursor:default" data-toggle="tooltip" data-placement="top" title="${escapeHtml(r.device_type)}">${r.device_icon} ${escapeHtml(r.device_type)}</p>`,
    },
    { index: 2, title: 'Device name', name: 'Denumire', orderable: true, visible: true, html: (r) => `<p>${escapeHtml(r.Denumire)}</p>` },
    {
      index: 3,
      title: 'Device owner',
      name: 'Prenume',
      orderable: true,
      visible: true,
      /* Aici scria `UNASSIGNED_TEXT` - vezi comentariul de sus (Roxana, 24
         septembrie 2026). Acum e butonul Assign, si textul ramane numai cand
         omul n-are dreptul de alocare. */
      html: (r) => ownerCellHtml(r.deviceId, r, canAssign),
    },
    { index: 4, title: 'Office', name: 'office', orderable: true, visible: true, html: (r) => textOrDash(r.office) },
    { index: 5, title: 'Department', name: 'department', orderable: true, visible: true, html: (r) => textOrDash(r.department) },
    { index: 6, title: 'Group', name: 'DeviceGroup', orderable: true, visible: true, html: (r) => textOrDash(r.DeviceGroup) },
    { index: 7, title: 'Brand', name: 'brand_name', orderable: true, visible: true, html: (r) => textOrDash(r.brand_name) },
    { index: 8, title: 'Model', name: 'Model', orderable: true, visible: true, html: (r) => textOrDash(r.Model) },
    { index: 9, title: 'Serial', name: 'Serial', orderable: true, visible: true, html: (r) => textOrDash(r.Serial) },
    { index: 10, title: 'Actions', orderable: false, visible: true, width: 115, html: (r) => r.actions || DASH },
    {
      index: 11,
      title: '',
      titleHtml: '<center><input type="checkbox" class="allDevices"/></center>',
      orderable: false,
      visible: true,
      width: 50,
      html: (r) => `<center><input value="${r.deviceId}" type="checkbox" class="oneDevice"${r.__checked ? ' checked' : ''}/></center>`,
    },
  ]
}

export interface LicenseRowView {
  ID: number
  short_name: string
  total: number | null
  active: number | null
  devices: number | null
  devices_licensed: number | null
  last_version: string | null
  category_name: string | null
  [key: string]: unknown
}

/** SoftwareLicensesModel din public/js/inventoree.js. */
export function licenseColumns(): Column[] {
  return [
    {
      index: 0,
      title: '',
      orderable: false,
      visible: true,
      width: 50,
      html: (r) =>
        `<center class="show_details color-dark-grey pointer font-16" data-id="${Number(r.ID)}"><i class="fas fa-plus-circle"></i></center>`,
    },
    { index: 1, title: 'Application Name', name: 'short_name', orderable: true, visible: true, html: (r) => escapeHtml(r.short_name) },
    {
      index: 2,
      title: 'Total Licenses',
      name: 'total',
      orderable: true,
      visible: true,
      html: (r) => (r.total ? `<i class="fa fa-star color-warning"></i> ${escapeHtml(r.total)}` : '<em>no license</em>'),
    },
    {
      index: 3,
      title: 'Active Licenses',
      name: 'active',
      orderable: true,
      visible: true,
      html: (r) => (r.active ? `<i class="fa fa-star color-green3"></i> ${escapeHtml(r.active)}` : '<em>no license</em>'),
    },
    {
      index: 4,
      title: 'Devices without license',
      name: 'devices',
      orderable: true,
      visible: true,
      html: (r) => {
        const devices = Number(r.devices || 0)
        const licensed = Number(r.devices_licensed || 0)
        return devices ? `<span class="badge bg-red-sunglo">${devices - licensed}</span> devices` : ''
      },
    },
    { index: 5, title: 'Last Version', name: 'last_version', orderable: true, visible: true, html: (r) => (r.last_version ? escapeHtml(r.last_version) : '') },
    {
      index: 6,
      title: 'Category',
      name: 'category_name',
      orderable: true,
      visible: true,
      html: (r) => (r.category_name ? escapeHtml(r.category_name) : '<em class="color-light-grey">no category</em>'),
    },
  ]
}

export interface UnassignedRowView {
  ID: number
  device_type: string
  device_icon: string
  Denumire: string
  UserLogat: string | null
  office: string | null
  DataAdaugare: string | null
  sLastIPIntern: string | null
  Mac: string | null
  external_ip: string | null
  location: string | null
  os_family: string | null
  device_role: string | null
  sAgentVersion: string | null
  matched: number
  DataAccesareDM: string | null
  actions: string
  __checked?: boolean
  [key: string]: unknown
}

/** OverviewUnassignedModel din public/js/overview.js. */
export function unassignedColumns(now: () => Date = () => new Date()): Column[] {
  return [
    {
      index: 0,
      title: '',
      orderable: false,
      visible: true,
      width: 50,
      html: (r) =>
        `<center class="show_details color-dark-grey pointer font-16" data-id="${Number(r.ID)}"><i class="fas fa-plus-circle"></i></center>`,
    },
    {
      index: 1,
      title: 'Type',
      name: 'Tip',
      orderable: true,
      visible: true,
      html: (r) =>
        `<center style="cursor:default" data-toggle="tooltip" data-placement="top" title="${escapeHtml(r.device_type)}">${r.device_icon}</center>`,
    },
    {
      index: 2,
      title: 'Device name',
      name: 'Denumire',
      orderable: true,
      visible: true,
      /* Lista Unknown: textul sta sub NUMELE aparatului, nu in coloana de
         proprietar, si aici toata pagina e a nealocatelor - deci ramane text.
         Scris din acelasi loc, ca sa nu se desparta de cel de sus. */
      html: (r) => `<p>${escapeHtml(r.Denumire)}</p>${UNASSIGNED_TEXT}${suggestionHtml(Number(r.ID), r.owner_suggestion)}`,
    },
    { index: 3, title: 'Last user logged on', name: 'UserLogat', orderable: true, visible: true, html: (r) => textOrDash(r.UserLogat) },
    { index: 4, title: 'Office', name: 'office', orderable: true, visible: true, html: (r) => textOrDash(r.office) },
    { index: 5, title: 'First seen', name: 'DataAdaugare', orderable: true, visible: true, html: (r) => (r.DataAdaugare ? escapeHtml(r.DataAdaugare) : DASH) },
    { index: 6, title: 'Internal IP', name: 'sLastIPIntern', orderable: true, visible: true, html: (r) => textOrDash(r.sLastIPIntern) },
    {
      index: 7,
      title: 'Mac',
      name: 'Mac',
      orderable: true,
      visible: true,
      html: (r) => (r.Mac ? `<span class="font-11">${escapeHtml(String(r.Mac).replace(/-/g, ':'))}</span>` : DASH),
    },
    { index: 8, title: 'External IP', name: 'external_ip', orderable: true, visible: true, html: (r) => textOrDash(r.external_ip) },
    {
      index: 9,
      title: 'Location',
      name: 'location_name',
      orderable: true,
      visible: true,
      html: (r) => (r.location ? `<i class="fas fa-map-marker-alt color-light-grey"></i> ${escapeHtml(r.location)}` : DASH),
    },
    {
      index: 10,
      title: 'Platform',
      orderable: true,
      visible: true,
      html: (r) => {
        const family = String(r.os_family || '')
        const role = String(r.device_role || '')
        if (!family && !role) return DASH
        return `<span>${escapeHtml(family)}</span>` + (role ? ` <span class="color-light-grey font-11">${escapeHtml(role)}</span>` : '')
      },
    },
    { index: 11, title: 'Agent', name: 'sAgentVersion', orderable: true, visible: true, html: (r) => textOrDash(r.sAgentVersion) },
    {
      index: 12,
      title: 'Matched devices',
      name: 'matched',
      orderable: true,
      visible: true,
      html: (r) => (Number(r.matched) > 0 ? `<span class="badge badge-success">${Number(r.matched)}</span>` : DASH),
    },
    {
      index: 13,
      title: 'Last communication',
      name: 'DataAccesareDM',
      orderable: true,
      visible: true,
      html: (r) => {
        const when = stamp(r.DataAccesareDM)
        return when ? fromNow(when, now()) : DASH
      },
    },
    {
      index: 14,
      title: '',
      titleHtml: '<center><input type="checkbox" class="allDevices"/></center>',
      orderable: false,
      visible: true,
      width: 50,
      html: (r) => `<center><input value="${Number(r.ID)}" type="checkbox" class="oneDevice"${r.__checked ? ' checked' : ''}/></center>`,
    },
    { index: 15, title: 'Actions', orderable: false, visible: true, html: (r) => (r.actions ? String(r.actions) : '') },
  ]
}

/**
 * Coloanele fisei standalone (#inventoreeDevice din device/details.php):
 * 18 coloane, fara plus / MAC / performance / last activity.
 */
export function standaloneColumns(now: () => Date = () => new Date(), canAssign = false): Column[] {
  const all = deviceColumns(now, canAssign)
  const keep = new Set([1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 18, 23, 24])
  return all.filter((c) => keep.has(c.index)).map((c, i) => ({ ...c, index: i }))
}
